import React, { useState } from 'react';
import { BestellungenHeader } from '../components';
import customFetch from '../utils/customFetch';
import { Link, useLoaderData } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PDFDownloadLink } from '@react-pdf/renderer';
import Invoice from '../components/dashboard/bestellungen/Invoice';

const orderQuery = {
  queryKey: ['orders'],
  queryFn: async () => {
    const data = await customFetch.get('/orders/showAllMyOrders');
    return data;
  },
};

export const loader = (queryClient) => async () => {
  const { data } = await queryClient.ensureQueryData(orderQuery);
  return data;
};

const Bestellungen = () => {
  const { data } = useQuery(orderQuery).data;

  // Kontrolliert ob Nutzer geklickt hat
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const handleDownloadClick = (orderId) => {
    // Set the selected order ID to the clicked one
    setSelectedOrderId(orderId);
  };

  return (
    <>
      <BestellungenHeader />
      {data.userOrders.length > 0 ? (
        <section aria-labelledby="recent-heading" className="mt-16">
          <h2 id="recent-heading" className="sr-only">
            Vergangene Bestellungen
          </h2>

          <div className="space-y-20">
            {data.userOrders.map((order) => (
              <div key={order._id}>
                <h3 className="sr-only">
                  Bestellung getätigt am{' '}
                  <time dateTime={order.createdAt}>
                    {order.createdAt.split('T')[0]}
                  </time>
                </h3>

                <div className="rounded-lg bg-gray-50 px-4 py-6 sm:flex sm:items-center sm:justify-between sm:space-x-6 sm:px-6 lg:space-x-8">
                  <dl className="flex-auto space-y-6 divide-y divide-gray-200 text-sm text-gray-600 sm:grid sm:grid-cols-2 md:grid-cols-4 sm:gap-6 sm:space-y-0 sm:divide-y-0 lg:w-3/4 lg:flex-none lg:gap-x-8">
                    <div className="flex justify-between sm:block">
                      <dt className="font-medium text-gray-900">
                        Bestelldatum
                      </dt>
                      <dd className="sm:mt-1">
                        <time dateTime={order.createdAt}>
                          {order.createdAt.split('T')[0]}
                        </time>
                      </dd>
                    </div>
                    <div className="flex justify-between pt-6 sm:block sm:pt-0">
                      <dt className="font-medium text-gray-900">
                        Bestellnummer
                      </dt>
                      <dd className="sm:mt-1 text-wrap">{order.orderNumber}</dd>
                    </div>
                    <div className="flex justify-between pt-6 sm:block sm:pt-0">
                      <dt className="font-medium text-gray-900">Status</dt>
                      <dd className="sm:mt-1 text-wrap">{order.status}</dd>
                    </div>
                    <div className="flex justify-between pt-6 font-medium text-gray-900 sm:block sm:pt-0">
                      <dt>Total Betrag</dt>
                      <dd className="sm:mt-1">CHF {order.total / 100}</dd>
                    </div>
                  </dl>
                  <div>
                    {selectedOrderId === order._id ? (
                      <PDFDownloadLink
                        document={<Invoice order={order} />}
                        fileName={`Rechnung_${
                          order.orderNumber || order._id
                        }.pdf`}
                        className="mt-6 flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-600 focus:ring-offset-2 sm:mt-0 sm:w-auto cursor-pointer"
                      >
                        {({ loading }) =>
                          loading
                            ? 'Rechnung wird erstellt...'
                            : 'Rechnung herunterladen'
                        }
                      </PDFDownloadLink>
                    ) : (
                      <button
                        onClick={() => handleDownloadClick(order._id)}
                        className="mt-6 flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-600 focus:ring-offset-2 sm:mt-0 sm:w-auto cursor-pointer"
                      >
                        Rechnung generieren
                      </button>
                    )}
                    <span className="sr-only">
                      Rechnung anzeigen für die Bestellung Nummer {order._id}
                    </span>
                  </div>
                </div>

                <table className="mt-4 w-full text-gray-500 sm:mt-6">
                  <caption className="sr-only">Produkte</caption>
                  <thead className="sr-only text-left text-sm text-gray-500 sm:not-sr-only">
                    <tr>
                      <th
                        scope="col"
                        className="py-3 pr-8 font-normal sm:w-2/5 lg:w-1/3"
                      >
                        Produkt
                      </th>
                      <th
                        scope="col"
                        className="hidden w-1/5 py-3 pr-8 font-normal sm:table-cell"
                      >
                        Preis
                      </th>
                      <th
                        scope="col"
                        className="hidden py-3 pr-8 font-normal sm:table-cell"
                      >
                        Menge
                      </th>
                      <th
                        scope="col"
                        className="w-0 py-3 text-right font-normal"
                      >
                        Info
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 border-b border-gray-200 text-sm sm:border-t">
                    {order.orderItems.map((product) => (
                      <tr key={product._id}>
                        <td className="py-6 pr-8">
                          <div className="flex items-center">
                            <img
                              src={product.image}
                              alt={product.name}
                              className="mr-6 h-16 w-16 rounded-sm object-cover object-center"
                            />
                            <div>
                              <div className="font-medium text-gray-900">
                                {product.name}
                              </div>
                              <div className="mt-1 sm:hidden">
                                CHF {product.price / 100}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="hidden py-6 pr-8 sm:table-cell">
                          CHF {product.price / 100}
                        </td>
                        <td className="hidden py-6 pr-8 sm:table-cell">
                          {product.amount}
                        </td>
                        <td className="whitespace-nowrap py-6 text-right font-medium">
                          <a
                            href={`/produkte/${product.product}`}
                            className="text-svisit-primary-600"
                          >
                            Produkt
                            <span className="hidden lg:inline"> ansehen</span>
                            <span className="sr-only">, {product.name}</span>
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </section>
      ) : (
        <div className="text-center px-6 py-10">
          <p className="text-lg leading-8 text-gray-600">
            Du hast noch keine Bestellung getätigt
          </p>
          <Link
            to="/produkte"
            className="block bg-svisit-primary-600 mt-3 max-w-fit mx-auto rounded-lg px-3 py-2 text-base font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
          >
            Zu den Produkten
          </Link>
        </div>
      )}
    </>
  );
};

export default Bestellungen;
